import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import GatewayPicker from 'components/gatewayPicker'
import ManualPaymentCard from 'components/manualPayment'
import useAvailableGateways from 'utilities/hooks/useAvailableGateways'
import { BoxRef } from 'services/easyPay/gateways'
import { faPlain, toman } from 'utilities/helper/format'
import useSetGatewayPayment from './hooks/useSetGatewayPeyment'

/**
 * «تایید و پرداخت» — the receipt for a basket of payables.
 *
 * Rebuilt to be the same screen as the loan repayment receipt. They are the
 * same act — here is what you owe, pick how to pay, pay — and they used to be
 * two unrelated designs: this one was a torn-paper SVG hand, an absolutely
 * positioned fixed-height card and a bare row of bank logos, with a CTA
 * labelled «پرداخت درگاهی(جدید)» and a receipt line reading «پرداخت آسان در
 * گت وی». That is the "شکل و شمایلش هم یکی نیست" half of the report.
 *
 * The other half was the gateway list. This screen asked for gateways without
 * telling the backend which funds it was paying, so it was offered Pasargad on
 * funds Pasargad is not live on and the pay button 400'd; the loan screen did
 * name its fund and was offered کارت‌به‌کارت alone, which the old picker then
 * dropped. Both now go through `useAvailableGateways`, which asks per-fund.
 */
export default function InitGatewayPayment() {
    const { state } = useLocation()
    const { mutate, isLoading: isPaying } = useSetGatewayPayment()

    const selected: any[] = useMemo(
        () => state?.toBePaidTransactions ?? [],
        [state]
    )

    // Ask about exactly the funds in the basket. `initiate_payment` requires
    // EVERY item's box to be allowed, so a gateway offered for a subset is a
    // gateway that will reject the payment.
    const boxes: BoxRef[] = useMemo(
        () =>
            selected.map((item) => ({
                type: item.box_type === 'csb' ? 'cbx' : 'mnp',
                id: Number(item.box_id),
            })),
        [selected]
    )

    const { gateways, selectedCode, setSelectedCode, hasNoGateway } =
        useAvailableGateways(boxes, { enabled: selected.length > 0 })

    const amount = Number(state?.total ?? 0)
    const commission = Number(state?.commission ?? 0)
    const count = selected.length

    // A basket that reached the receipt and has nowhere to go. Counted because
    // it is a dead end we cannot see from the funnel: the payer neither pays
    // nor fails, they just stop.
    const hasManualFallback = !!selected[0]?.manual_payment?.phone
    useEffect(() => {
        if (!hasNoGateway) return
        posthog.capture('payment_no_gateway_available', {
            flow_type: 'gateway_payment',
            has_manual_fallback: hasManualFallback,
        })
    }, [hasNoGateway, hasManualFallback])

    const onPay = () => {
        if (!selectedCode) {
            toast.error('روش پرداخت را انتخاب کنید')
            return
        }
        posthog.capture('payables_gateway_selected', {
            gateway: selectedCode,
            items_count: count,
            total: amount + commission,
        })
        mutate({
            paymentItems: selected.map((item) => ({
                itemAmount: item.amount,
                itemType: item.type,
                itemId: item.id,
                box_id: item.box_id,
                commission: item.commission,
                description: item.description,
            })),
            gateway: { uniqueCode: selectedCode },
            clientType: 'pwa',
        })
    }

    if (!state || count === 0) return <DotsLoading />

    return (
        <div className="px-4 pt-2 pb-8">
            <div className="bg-white border border-divider rounded-card shadow-card p-4">
                <p className="text-xs text-text-secondary text-center">
                    قابل پرداخت
                </p>
                <p className="mt-[2px] text-center text-[26px] font-bold text-heading leading-tight">
                    {toman(amount + commission)}
                </p>

                <div className="mt-4 flex flex-col gap-2 border-t border-divider pt-3">
                    <Row
                        label={`مبلغ ${faPlain.format(count)} مورد`}
                        value={toman(amount)}
                    />
                    <Row label="هزینه اشتراک" value={toman(commission)} />
                </div>

                <div className="mt-3 border-t border-divider pt-3">
                    <p className="mb-2 text-[13px] text-text-secondary">
                        موارد این پرداخت
                    </p>
                    <div className="flex flex-col gap-1.5">
                        {selected.map((item) => (
                            <div
                                key={`${item.type}-${item.box_id}-${item.id}`}
                                className="flex items-baseline justify-between gap-3"
                            >
                                <span className="text-sm text-text-body shrink-0 truncate">
                                    {item.box_name}
                                </span>
                                <span className="text-sm font-medium text-text-body truncate">
                                    {toman(item.amount + item.commission)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-5">
                {hasNoGateway ? (
                    // Reached when every fund in the basket is off every
                    // gateway. Before, this was a screen with a pay button
                    // that could only ever fail. A record-mode fund never gets
                    // here — its rows cannot be ticked — so the manual details
                    // are usually absent and the honest answer is that the
                    // online route is down, not that this fund never had one.
                    selected[0]?.manual_payment?.phone ? (
                        <ManualPaymentCard
                            info={selected[0].manual_payment}
                            boxName={selected[0].box_name}
                            source="payables_receipt"
                        />
                    ) : (
                        <div className="bg-white border border-divider rounded-card p-4 text-center">
                            <p className="text-sm font-bold text-heading">
                                پرداخت آنلاین در دسترس نیست
                            </p>
                            <p className="mt-1 text-[13px] leading-6 text-text-secondary">
                                در حال حاضر هیچ روش پرداختی برای این صندوق‌ها
                                فعال نیست. کمی بعد دوباره تلاش کنید یا با مدیر
                                صندوق هماهنگ کنید.
                            </p>
                        </div>
                    )
                ) : (
                    <>
                        <GatewayPicker
                            gateways={gateways}
                            selectedCode={selectedCode}
                            onSelect={setSelectedCode}
                        />
                        <button
                            type="button"
                            onClick={onPay}
                            disabled={isPaying}
                            className="mt-6 w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none disabled:opacity-60"
                        >
                            {isPaying
                                ? 'در حال انتقال…'
                                : `پرداخت ${toman(amount + commission)}`}
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

/** Label on the right, value on the left — the page is already RTL. */
function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-text-secondary shrink-0">
                {label}
            </span>
            <span className="text-[15px] font-medium text-text-body truncate">
                {value}
            </span>
        </div>
    )
}
